import React, { useState } from "react";
import { X, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr, assetUrl } from "../../lib/api";

const EnquiryDialog = ({ open, onClose, context = {} }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const close = () => { setSent(false); setForm({ name: "", email: "", phone: "", message: "" }); onClose(); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", {
        ...form,
        parcel_id: context.parcel_id || null,
        stage_id: context.stage_id || null,
        source: context.source || "website",
      });
      setSent(true);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="enquiry-dialog">
      <div className="absolute inset-0 bg-[#0C1510]/80 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-lg bg-[#12201A] border border-[#F3EEE3]/15 rounded-2xl p-8">
        <button onClick={close} className="absolute top-4 right-4 text-[#F3EEE3]/60 hover:text-[#F3EEE3]" aria-label="Close">
          <X size={20} />
        </button>

        {sent ? (
          <div className="text-center py-4" data-testid="enquiry-confirmation">
            {context.builderLogo ? (
              <div className="mx-auto w-24 h-24 rounded-2xl overflow-hidden border border-[#F3EEE3]/15 shadow-lg mb-5">
                <img src={assetUrl(context.builderLogo)} alt={context.builderName || ""} className="w-full h-full object-cover" />
              </div>
            ) : (
              <CheckCircle2 size={52} className="mx-auto text-[#C9B79C] mb-4" />
            )}
            <h3 className="font-display text-2xl text-[#F3EEE3]">Thank you — enquiry received</h3>
            <p className="text-[#F3EEE3]/65 text-sm mt-3 max-w-sm mx-auto">
              {context.builderName
                ? <>Our land team will connect you with <span className="text-[#F3EEE3] font-medium">{context.builderName}</span> about {context.label || "your enquiry"}.</>
                : <>Our land team will be in touch about {context.label || "your enquiry"} shortly.</>}
            </p>
            <button onClick={close} data-testid="enquiry-done" className="mt-7 inline-flex items-center justify-center gap-2 bg-[#F3EEE3] text-[#12201A] px-7 py-3 rounded-full font-medium hover:bg-white transition-colors">
              Done
            </button>
          </div>
        ) : (
          <>
            <p className="eyebrow text-[#C9B79C] mb-2">Request Information</p>
            <div className="flex items-center gap-3 mb-1">
              {context.builderLogo && (
                <div className="w-11 h-11 rounded-lg overflow-hidden border border-[#F3EEE3]/15 shrink-0">
                  <img src={assetUrl(context.builderLogo)} alt={context.builderName ? `${context.builderName} logo` : "Builder logo"} className="w-full h-full object-cover" />
                </div>
              )}
              <h3 className="font-display text-2xl text-[#F3EEE3]">Speak with our land team</h3>
            </div>
            {context.label && <p className="text-[#F3EEE3]/55 text-sm mb-5">Regarding: {context.label}</p>}
            <form onSubmit={submit} className="space-y-4 mt-4">
              {["name", "email", "phone"].map((k) => (
                <input
                  key={k}
                  required={k !== "phone"}
                  type={k === "email" ? "email" : "text"}
                  value={form[k]}
                  onChange={set(k)}
                  placeholder={k[0].toUpperCase() + k.slice(1) + (k === "phone" ? " (optional)" : "")}
                  data-testid={`enquiry-${k}`}
                  className="w-full bg-[#0C1510] border border-[#F3EEE3]/15 rounded-lg px-4 py-3 text-[#F3EEE3] placeholder:text-[#F3EEE3]/35 focus:border-[#C9B79C] outline-none"
                />
              ))}
              <textarea
                value={form.message}
                onChange={set("message")}
                rows={3}
                placeholder="Your message"
                data-testid="enquiry-message"
                className="w-full bg-[#0C1510] border border-[#F3EEE3]/15 rounded-lg px-4 py-3 text-[#F3EEE3] placeholder:text-[#F3EEE3]/35 focus:border-[#C9B79C] outline-none"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="enquiry-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#F3EEE3] text-[#12201A] px-6 py-3.5 rounded-full font-medium hover:bg-white transition-colors disabled:opacity-60"
              >
                <Send size={16} /> {busy ? "Sending…" : "Send enquiry"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default EnquiryDialog;
